// The one way this app talks to the BFF — TODO T17 (30 Aug 2026), split out at T30 (1 Sep 2026).
//
// WHY THIS FILE IS SEPARATE FROM THE DOMAIN MODULES. Until T30 the whole client and every query
// hook lived in two files (`client.ts`, `queries.ts`), and both were a list every brain task
// appends to. Two brain lanes in one round would collide on those lists — so the transport is
// here, and each domain gets `api/<domain>.ts` holding its own calls and its own hooks (design
// doc § 1.7). A later lane adds a FILE.
//
// TYPES COME FROM THE SERVICE, NOT FROM HERE. Every response shape is imported from `@vera/bff`,
// which exports them for exactly this reason. Re-declaring them would make the SPA's idea of a
// search and the service's idea of one two facts that agree until they do not.
//
// The imports are `import type` and stay that way, in every module. `@vera/bff`'s barrel pulls in
// Fastify, pg and @clerk/backend at runtime; under `verbatimModuleSyntax` a type import is erased
// entirely, so none of that can reach a browser bundle. The one runtime constant this file needs
// — the `/api/v1` prefix — is restated below and pinned to the service's by a test, which is the
// cheap version of the same guarantee.

/**
 * Restated rather than imported — see the file header. `core.test.ts` asserts it equals
 * `@vera/bff`'s `API_PREFIX`, so the two cannot drift without CI saying so.
 */
export const API_PREFIX = '/api/v1';

/**
 * Why the service refused, in the brain's vocabulary.
 *
 * A plain `string` rather than a union of the service's codes (T30): the union had to be widened
 * in this file every time a route learned a new refusal, which put every domain back in one
 * place. `ERROR_CODES` below is what the brain will *render* a sentence for; anything else is
 * reported as itself, which is still more useful to a recruiter than "something went wrong".
 */
export type ApiErrorCode = string;

/**
 * Every failure the brain can render, as one type.
 *
 * `not_a_recruiter` is the one worth naming: it is a 403 from a session that is perfectly valid,
 * and it is the state a newly invited recruiter is in until an operator links their Clerk user to
 * a `staff_operators` row. Treating it as "signed out" would loop them through Clerk forever, so
 * the app gives it a screen of its own (app/Gate.tsx).
 */
export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly status: number;
  readonly code: ApiErrorCode;
  /**
   * The field-naming reasons behind a refusal, when the service chose to send them — the
   * candidate entry form's parser messages and nothing else (services/bff/src/body.ts § 4).
   * Empty for every other refusal, whose reasons stay in the service's log.
   */
  readonly problems: readonly string[];

  constructor(status: number, code: ApiErrorCode, message: string, problems: string[] = []) {
    super(message);
    this.status = status;
    this.code = code;
    this.problems = problems;
  }
}

/**
 * The codes this app knows how to explain. The first eighteen are the service's `ErrorResponse`
 * codes verbatim; the last four are conditions the browser sees and the service never sends.
 * A code outside this set is still surfaced — as itself (ui/states.tsx, ui/forms.tsx).
 */
export const ERROR_CODES = new Set<string>([
  'unauthenticated',
  'not_a_recruiter',
  'not_found',
  'invalid_id',
  'invalid_cursor',
  'bad_request',
  'conflict',
  // T330c: a delete refused while somebody linked to the row is being deleted.
  'being_deleted',
  'email_unavailable',
  // T35: the find's three — no key on the server, the model's answer unusable, too many asks.
  'find_unavailable',
  // T67: no OpenAI key on the server, or OpenAI refused to open a voice session.
  'voice_unavailable',
  // T53a: no contact provider's key on the server.
  'contacts_unavailable',
  // T82: a removal's card, confirmed by one yes — neither its button nor, since 23 Sep 2026,
  // a second yes.
  'second_yes_required',
  'find_failed',
  'rate_limited',
  'internal',
  // T561: a Reply whose message, or whose highlighted words, this chat does not hold; and a
  // question on a search no longer live, which the find answered before this list knew it.
  'reply_not_found',
  'search_not_live',
  'no_token',
  'network',
  'unreadable',
  // 17 Sep 2026: a streamed find that failed after its answer was written (api/find.ts).
  'find_incomplete',
]);

/** What a domain module is handed. Nothing below `api/` reaches `fetch` on its own. */
/**
 * `binary` (T66): the response is bytes, not JSON — her voice's audio — handed over as an
 * ArrayBuffer; `T` is then `ArrayBuffer`. Every other call is JSON in and JSON out.
 */
export type RequestFn = {
  <T>(path: string, init?: { method?: string; body?: unknown; binary?: boolean }): Promise<T>;
  /**
   * T160: the release this page was built from, which the two imports send so the API can tell
   * a page from another release to reload. Absent in tests and the harness.
   */
  release?: string | null;
};

/**
 * A streamed request (T66b): one POST whose answer is newline-delimited JSON, each line handed
 * over as it arrives — the find as voice mode hears it. Resolves when the response ends; a
 * refusal before the first byte is an `ApiError` like any other request's.
 */
export type StreamFn = (
  path: string,
  body: unknown,
  onLine: (line: unknown) => void,
) => Promise<void>;

export interface ApiOptions {
  /** Origin only, no trailing slash — env.ts enforces that. */
  baseUrl: string;
  /**
   * Clerk's `getToken` from `useAuth()`. Called per request rather than held: Clerk rotates the
   * session token roughly every minute, and a token captured once would start failing `exp` in
   * the middle of a polling loop.
   */
  getToken: () => Promise<string | null>;
  /** Injected so the suite can drive this without a browser or a server. */
  fetchImpl?: typeof fetch;
  /** T160: VITE_RELEASE, carried on the request function for the imports (env.ts). */
  release?: string | null;
}

/** Renders `?a=1&b=2`, dropping anything it was not given rather than sending it empty. */
export function query(
  params: Record<string, string | number | boolean | readonly string[] | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    // A list is the key repeated (T121's `answer=country:SE&answer=seniority:vp`).
    if (Array.isArray(value)) for (const item of value) search.append(key, item);
    else if (value !== undefined) search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered === '' ? '' : `?${rendered}`;
}

export function createRequest(options: ApiOptions): RequestFn {
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);

  const request = async function request<T>(
    path: string,
    init: { method?: string; body?: unknown; binary?: boolean } = {},
  ): Promise<T> {
    const token = await options.getToken();
    if (!token) {
      // Clerk has no session, so there is nothing to send. Failing here rather than sending a
      // bare request keeps a signed-out poll from looking like a rejected one in the BFF's logs.
      throw new ApiError(0, 'no_token', 'no Clerk session token');
    }

    const method = init.method ?? 'GET';
    let response: Response;
    try {
      response = await doFetch(`${options.baseUrl}${path}`, {
        method,
        headers: {
          // A bearer token, never a cookie: the BFF accepts no cookie and sets none, and the
          // brain is a different origin from it (services/bff/src/auth.ts).
          authorization: `Bearer ${token}`,
          accept: init.binary ? '*/*' : 'application/json',
          ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
        },
        ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
        // The request carries its own credentials in that header; asking the browser to attach
        // ambient ones would need CORS credentials and buy a CSRF surface for nothing.
        credentials: 'omit',
      });
    } catch {
      throw new ApiError(0, 'network', `could not reach the brain API at ${options.baseUrl}`);
    }

    if (!response.ok) {
      const refusal = await readRefusal(response);
      throw new ApiError(
        response.status,
        refusal.code,
        `${response.status} ${path}`,
        refusal.problems,
      );
    }

    // Every write answers 204 with no body (T18). Parsing that as JSON would throw and be
    // reported as `unreadable` — a successful edit that looks like a broken one, which is worse
    // than either.
    if (response.status === 204) return undefined as T;

    if (init.binary) {
      try {
        return (await response.arrayBuffer()) as T;
      } catch {
        throw new ApiError(response.status, 'unreadable', `${path} did not return its bytes`);
      }
    }

    try {
      return (await response.json()) as T;
    } catch {
      throw new ApiError(response.status, 'unreadable', `${path} did not return JSON`);
    }
  };
  // T160: the page's release rides on the function, for the two imports that send it.
  return Object.assign(request, { release: options.release ?? null });
}

/**
 * The service answers every refusal as `{ "error": "<code>" }` and says nothing else — the detail
 * is a log line there, deliberately. So a body that is not one of those codes is treated as one
 * more way of being unreadable rather than shown to a recruiter.
 *
 * The one exception (8 Sep 2026): a `problems` list of strings beside a known code, which the
 * service sends for the candidate entry form alone and the error box renders verbatim. Taken only
 * when the code itself is trusted, and only as strings — anything else is dropped, not shown.
 */
async function readRefusal(
  response: Response,
): Promise<{ code: ApiErrorCode; problems: string[] }> {
  try {
    const body = (await response.json()) as { error?: unknown; problems?: unknown };
    if (typeof body.error === 'string' && ERROR_CODES.has(body.error)) {
      const problems = Array.isArray(body.problems)
        ? body.problems.filter((item): item is string => typeof item === 'string')
        : [];
      return { code: body.error, problems };
    }
  } catch {
    // An ALB 502 or a CloudFront error page is HTML, not JSON.
  }
  return { code: response.status === 401 ? 'unauthenticated' : 'unreadable', problems: [] };
}

export function createStreamRequest(options: ApiOptions): StreamFn {
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);

  return async function stream(path, body, onLine) {
    const token = await options.getToken();
    if (!token) throw new ApiError(0, 'no_token', 'no Clerk session token');

    let response: Response;
    try {
      response = await doFetch(`${options.baseUrl}${path}`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
          accept: 'application/x-ndjson',
          'content-type': 'application/json',
        },
        body: JSON.stringify(body),
        credentials: 'omit',
      });
    } catch {
      throw new ApiError(0, 'network', `could not reach the brain API at ${options.baseUrl}`);
    }
    if (!response.ok) {
      const refusal = await readRefusal(response);
      throw new ApiError(
        response.status,
        refusal.code,
        `${response.status} ${path}`,
        refusal.problems,
      );
    }
    if (!response.body) throw new ApiError(response.status, 'unreadable', `${path} sent no body`);

    // Lines as they complete; a line cut by a chunk boundary waits for its rest.
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let rest = '';
    const take = (text: string) => {
      rest += text;
      let at = rest.indexOf('\n');
      while (at !== -1) {
        const line = rest.slice(0, at).trim();
        rest = rest.slice(at + 1);
        if (line !== '') onLine(JSON.parse(line));
        at = rest.indexOf('\n');
      }
    };
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      take(decoder.decode(value, { stream: true }));
    }
    take(decoder.decode());
    if (rest.trim() !== '') onLine(JSON.parse(rest.trim()));
  };
}
